"use client";

import { useEffect, useState } from "react";

import type { WidgetConfig } from "./widget-config";
import type { QuickRepliesPublicConfig } from "@/lib/widget/quick-replies-public-config";
import { parseQuickRepliesPublicConfig } from "@/lib/widget/quick-replies-public-config";

export type WidgetCtaConfig = {
  readonly ctaEnabled: boolean;
  readonly ctaUrl: string | null;
  readonly ctaLabel: string;
  readonly ctaLeadThreshold: number;
};

const DEFAULT_CTA: WidgetCtaConfig = {
  ctaEnabled: false,
  ctaUrl: null,
  ctaLabel: "Schedule a demo",
  ctaLeadThreshold: 0.5,
};

export function useWidgetSiteExtras(config: WidgetConfig): {
  readonly cta: WidgetCtaConfig;
  readonly quickReplies: QuickRepliesPublicConfig;
  /** Site-owner has disabled server-side chat persistence for this site (#118). */
  readonly chatPersistenceDisabled: boolean;
  /** Manage → Chat Prompts enabled — when false, widget must not call /suggest. */
  readonly intentAssistEnabled: boolean;
} {
  const [cta, setCta] = useState<WidgetCtaConfig>(DEFAULT_CTA);
  const [quickReplies, setQuickReplies] = useState<QuickRepliesPublicConfig>({
    enabled: false,
    items: [],
  });
  const [chatPersistenceDisabled, setChatPersistenceDisabled] = useState<boolean>(false);
  const [intentAssistEnabled, setIntentAssistEnabled] = useState(false);

  useEffect(() => {
    const base = config.apiOrigin.replace(/\/$/, "");
    const extrasUrl = `${base}/api/engage/widget/site-extras?${new URLSearchParams({
      tenant_id: config.tenantId,
      site_id: config.siteId,
    }).toString()}`;
    const autolaunchUrl = `${base}/api/engage/autolaunch?${new URLSearchParams({
      tenant_id: config.tenantId,
      site_id: config.siteId,
    }).toString()}`;

    let cancelled = false;

    void (async () => {
      try {
        const [extrasRes, autoRes] = await Promise.all([
          fetch(extrasUrl, { credentials: "omit" }),
          fetch(autolaunchUrl, { credentials: "omit" }),
        ]);
        if (cancelled) return;

        if (extrasRes.ok) {
          const j = (await extrasRes.json()) as Record<string, unknown>;
          setCta({
            ctaEnabled: j.cta_enabled === true,
            ctaUrl: typeof j.cta_url === "string" ? j.cta_url : null,
            ctaLabel: typeof j.cta_label === "string" ? j.cta_label : DEFAULT_CTA.ctaLabel,
            ctaLeadThreshold:
              typeof j.cta_lead_threshold === "number" ? j.cta_lead_threshold : DEFAULT_CTA.ctaLeadThreshold,
          });
          setChatPersistenceDisabled(j.chat_persistence_disabled === true);
          setIntentAssistEnabled(j.intent_assist_enabled === true);
        }

        if (autoRes.ok) {
          const j = (await autoRes.json()) as Record<string, unknown>;
          setQuickReplies(parseQuickRepliesPublicConfig(j.quick_replies));
        }
      } catch (err) {
        console.warn("[useWidgetSiteExtras] fetch_failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config.apiOrigin, config.siteId, config.tenantId]);

  return { cta, quickReplies, chatPersistenceDisabled, intentAssistEnabled };
}
